<template lang="pug">
.reservation-report-table-component
  .title 面会予約

  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません

    // セル：種別
    template(#cell(type)="row")
      .d-flex.align-items-center
        .type.center(v-if="row.item.type == 0")
          .icon-offline(title="訪問面会")
        .type.center(v-else)
          .icon-online(title="オンライン面会")
    // セル：入力
    template(#cell(inputType)="row")
      .input-type.center(v-if="row.item.inputType == 0") 代理
      .input-type.center(v-else) 本人
    // セル：状態
    template(#cell(status)="row")
      .status.center.status-consented(v-if="row.item.status == true") 承諾済
      .status.center.status-no-consented(v-else) 未承諾
    // セル：面会日時
    template(#cell(datetime)="row")
      span {{ getDate(row.item.datetime) }}
      span （
      span(:class="weekCssClass(row.item.datetime)") {{ getWeek(row.item.datetime) }}
      span ）
      span {{ getTime(row.item.datetime) }}
    // セル：利用者名
    template(#cell(resident)="row")
      span.resident {{ row.item.resident.name }}（{{ row.item.resident.room }}）
    // セル：面会者名
    template(#cell(family)="row")
      span.family {{ row.item.family.name }}（{{ row.item.family.relation }}）
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { InputType, ReservationType } from "@/commons/services/Reservations";

/**
 * ### 活動履歴(面会予約)テーブルコンポーネント
 */
@Component({})
export default class ReservationReportPreviewTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<ReservationReportPreviewTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<ReservationReportPreviewTableField>;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;

  get _value(): Array<ReservationReportPreviewTableItem> {
    return this.value;
  }

  /**
   * 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(date: number): string {
    if (date) {
      return this.$dateUtil.convertNumberToString(date, "yyyy年mm月dd日");
    } else {
      return "";
    }
  }

  /**
   * 曜日を返す
   */
  public getWeek(date: number): string {
    if (date) {
      const dateObject: Date = this.$dateUtil.convertNumberToDate(date);
      return this.$dateUtil.getWeekName(dateObject, "ja");
    } else {
      return "";
    }
  }

  /**
   * 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(date: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(date);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * 時分を返す
   */
  public getTime(date: number): string {
    if (date) {
      return this.$dateUtil.convertNumberToString(date, "hh:MM");
    } else {
      return "";
    }
  }
}

/** テーブルの列名の情報 */
export interface ReservationReportPreviewTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface ReservationReportPreviewTableItem {
  /** 面会種別 */
  type: ReservationType;
  /** 予約入力 */
  inputType: InputType;
  /** 予約状態 */
  status: boolean;
  /** 面会日時 */
  datetime: number;
  /** 面会場所 */
  location: string;
  /** 利用者グループ */
  residentGroupName: string;
  /** 利用者名（部屋名） */
  resident: { name: string; room: string; id: number };
  /** 面会者名（続柄） */
  family: { name: string; relation: string; id: number };
  /** 家族からの連絡事項 */
  text: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_mixin.scss";
@import "../../../styles/pc/_colors.scss";

.reservation-report-table-component {
  .icon-offline {
    @include svg-icon("../../../assets/reservation/offline.svg", $font-base-color);
  }
  .icon-online {
    @include svg-icon("../../../assets/reservation/online.svg", $font-base-color);
  }

  .title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 4px;
  }

  .status-consented {
    color: $font-disable-font-color;
    font-weight: bold;
  }
  .status-no-consented {
    color: $font-strong-color;
    font-weight: bold;
  }
}
</style>
